import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';

/**
 * Minimal name-this-item form shown after the user draws a bbox
 * for a Vision-missed item. Deliberately lighter than
 * {@link EditItemSheet} — no id, no update call, just a name/
 * category/value gather that the caller turns into a
 * `ManualAddInventoryItemRequest`.
 *
 * Save is disabled until `name` is non-blank (backend enforces
 * `item_name` as required).
 */

export interface NameDraftValues {
  item_name: string;
  item_category?: string;
  estimated_value?: number;
}

interface NameDraftItemSheetProps {
  visible: boolean;
  saving?: boolean;
  onCancel: () => void;
  onSave: (values: NameDraftValues) => void;
}

export const NameDraftItemSheet: React.FC<NameDraftItemSheetProps> = ({
  visible,
  saving = false,
  onCancel,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [value, setValue] = useState('');

  // Reset fields whenever the sheet re-opens. Prevents a previous
  // item's name leaking into the next draft.
  useEffect(() => {
    if (visible) {
      setName('');
      setCategory('');
      setValue('');
    }
  }, [visible]);

  const canSave = name.trim().length > 0 && !saving;

  // Keep the latest callbacks + canSave in refs so the web-only
  // keydown listener doesn't re-bind on every keystroke.
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;
  const nameRef = useRef(name);
  nameRef.current = name;
  const categoryRef = useRef(category);
  categoryRef.current = category;
  const valueRef = useRef(value);
  valueRef.current = value;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const commit = useCallback(() => {
    if (!canSaveRef.current) return;
    const parsedValue = valueRef.current.trim()
      ? Number(valueRef.current.replace(/[^0-9.]/g, ''))
      : NaN;
    onSaveRef.current({
      item_name: nameRef.current.trim(),
      item_category: categoryRef.current.trim() || undefined,
      estimated_value: Number.isFinite(parsedValue) ? parsedValue : undefined,
    });
  }, []);

  // Web-only: RN Modal's `onRequestClose` fires for Android back,
  // NOT for Escape. Wire our own keydown listener so Escape closes
  // the sheet and Enter (with a non-blank name) submits.
  useEffect(() => {
    if (Platform.OS !== 'web' || !visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancelRef.current();
      } else if (e.key === 'Enter') {
        // Only swallow Enter when we have something to submit —
        // otherwise forms nested in the app might eat a legit
        // newline keystroke.
        if (canSaveRef.current) {
          e.preventDefault();
          commit();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, commit]);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.header}>
            <View style={styles.headerIcon}>
              <MaterialIcons
                name="add-box"
                size={18}
                color={fpColors.primary}
              />
            </View>
            <Text style={styles.title}>Name this item</Text>
            <Pressable
              onPress={onCancel}
              hitSlop={8}
              style={styles.closeBtn}
              accessibilityLabel="Cancel add"
            >
              <MaterialIcons
                name="close"
                size={18}
                color={fpColors.onSurfaceVariant}
              />
            </Pressable>
          </View>

          <Text style={styles.subtitle}>
            We&apos;ll save this exact box as the item&apos;s mask.
          </Text>

          <View style={styles.field}>
            <Text style={styles.label}>Name</Text>
            <TextInput
              value={name}
              onChangeText={setName}
              placeholder="e.g. Blue mug"
              placeholderTextColor={fpColors.outline}
              style={styles.input}
              autoFocus
              returnKeyType="done"
              onSubmitEditing={commit}
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Category (optional)</Text>
            <TextInput
              value={category}
              onChangeText={setCategory}
              placeholder="e.g. kitchenware"
              placeholderTextColor={fpColors.outline}
              style={styles.input}
              returnKeyType="next"
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Estimated value (optional)</Text>
            <TextInput
              value={value}
              onChangeText={setValue}
              placeholder="0"
              placeholderTextColor={fpColors.outline}
              style={styles.input}
              keyboardType="numeric"
              returnKeyType="done"
              onSubmitEditing={commit}
            />
          </View>

          <View style={styles.actions}>
            <Pressable
              onPress={onCancel}
              disabled={saving}
              style={({ pressed }) => [
                styles.btn,
                styles.btnSecondary,
                pressed && { opacity: 0.85 },
                saving && { opacity: 0.6 },
              ]}
              accessibilityRole="button"
            >
              <Text style={styles.btnSecondaryText}>Cancel</Text>
            </Pressable>
            <Pressable
              onPress={commit}
              disabled={!canSave}
              style={({ pressed }) => [
                styles.btn,
                styles.btnPrimary,
                !canSave && { opacity: 0.5 },
                pressed && canSave && { opacity: 0.9 },
              ]}
              accessibilityRole="button"
            >
              {saving ? (
                <ActivityIndicator size="small" color={fpColors.onPrimary} />
              ) : (
                <MaterialIcons
                  name="check"
                  size={16}
                  color={fpColors.onPrimary}
                />
              )}
              <Text style={styles.btnPrimaryText}>
                {saving ? 'Adding…' : 'Add item'}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.35)',
    justifyContent: 'center',
    padding: 16,
  },
  sheet: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 16,
    padding: 18,
    maxWidth: 420,
    alignSelf: 'center',
    width: '100%',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 20px 48px rgba(31,26,34,0.25)' } as any)
      : null),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  headerIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}14`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onSurface,
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  subtitle: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 17,
    color: fpColors.onSurfaceVariant,
    marginBottom: 14,
  },
  field: {
    marginBottom: 12,
    gap: 4,
  },
  label: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: fpColors.onSurfaceVariant,
  },
  input: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    color: fpColors.onSurface,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : null),
  },
  actions: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 4,
  },
  btn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 999,
  },
  btnSecondary: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  btnSecondaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  btnPrimary: {
    backgroundColor: fpColors.primary,
    flex: 1.3,
  },
  btnPrimaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimary,
  },
});
